import React, { createContext, useContext, useEffect, useState } from 'react';
import { authClient } from '../lib/auth-client';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any;
  setIsAuthenticated: (value: boolean) => void;
  setUser: (user: any) => void;
  clearUser: () => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useBetterAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useBetterAuth must be used within a BetterAuthProvider');
  }
  return context;
};

const BetterAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);

  const clearUser = () => {
    setIsAuthenticated(false);
    setUser(null);
  };

  const logout = async () => {
    try {
      await authClient.signOut();
      clearUser();
    } catch (error) {
      console.error('Error during logout:', error);
      // Clear local state even if server logout fails
      clearUser();
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if user has a valid session using better-auth
        const session = await authClient.getSession();

        if (session.data?.session && session.data?.user) {
          // User is authenticated via cookie
          setIsAuthenticated(true);
          setUser(session.data.user);
        } else {
          // No valid session
          clearUser();
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        clearUser();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const value = {
    isAuthenticated,
    isLoading,
    user,
    setIsAuthenticated,
    setUser,
    clearUser,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default BetterAuthProvider;
